// AccountApi — the granular `/generic/*` account calls used for targeted context reloads
// (contract 07 §6, canonical api/ contract 03 §15). Imports ONLY an http instance + own
// models and returns the unwrapped DTO, never an AxiosResponse (fixes the v3 `{data:{data}}`
// leak — ledger A3).
//
// BE-07-01: these endpoints disagree on envelope shape. `/generic/user/`,
// `.../list-accounts` and `/generic/user_access_info` return the payload flat; only
// `/generic/accessible-accounts/` nests it under `{ data: [...] }`. Each method normalizes
// its own shape so callers see a plain DTO.
import type { AxiosInstance } from 'axios'

import type {
  AccountDetail,
  AccountItem,
  UserAccessInfo,
  UserResponse,
} from '../models/account.types'

import { appHttp, unwrap } from '@/shared/http'

export class AccountApi {
  // Injectable for tests; defaults to the singleton instance.
  constructor(private readonly http: AxiosInstance = appHttp) {}

  /** GET /generic/user/ — body is the user object (flat). */
  async getMe(): Promise<UserResponse> {
    return unwrap(await this.http.get<UserResponse>('/generic/user/'))
  }

  /** GET /generic/accessible-accounts/ — body is `{ data: AccountItem[] }`. */
  async getAccessibleAccounts(): Promise<AccountItem[]> {
    const body = unwrap(
      await this.http.get<{ data?: AccountItem[] }>('/generic/accessible-accounts/'),
    )
    return body?.data ?? []
  }

  /** GET /generic/accessible-accounts/{id}/list-accounts — body is the AccountDetail (flat). */
  async getSelectedAccountDetail(accountId: number): Promise<AccountDetail | null> {
    return unwrap(
      await this.http.get<AccountDetail | null>(
        `/generic/accessible-accounts/${accountId}/list-accounts`,
      ),
    )
  }

  /** GET /generic/user_access_info — body is the UserAccessInfo (flat). */
  async getUserAccessInfo(): Promise<UserAccessInfo> {
    return unwrap(await this.http.get<UserAccessInfo>('/generic/user_access_info'))
  }

  /**
   * PATCH /auth/change-username/{userId} — body `{ new_username }` (v3 `AuthApi.ts:72-76`). The
   * response body is the updated user echo and no caller reads it, so this returns `void`.
   *
   * The PATH sits under `/auth/`, the SEAM does not: contract 06 §3 drops
   * `AuthService.updateUserName` ("username change moves to account, module 07") because its
   * static wrapper dynamic-imported `accountStore` to fill the id in. 07 owns it now (CM-22-05),
   * and the id arrives as an argument.
   *
   * The ONLY method here that suppresses the global handler: the reads above are boot-path fetches
   * whose failure nobody else reports, while this is a user-initiated WRITE whose caller (22's
   * profile pane) renders its own failure copy — without the flag one failed rename toasts twice.
   */
  async changeUsername(userId: number, newUsername: string): Promise<void> {
    await this.http.patch(
      `/auth/change-username/${userId}`,
      { new_username: newUsername },
      { suppressGlobalErrorHandler: true },
    )
  }
}

export const accountApi = new AccountApi()
